import type { StatusKey } from "@/lib/status";

/**
 * Typed client for the Happy24 Worker API (same-origin at /api/*). Auth is a
 * session cookie, so every call sends credentials. Non-2xx throws an ApiError;
 * 401 means the session lapsed and the caller should route to sign-in.
 */

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) message = body.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/* ----------------------------- types ----------------------------- */

export type Dashboard = {
  greeting_name: string;
  sobriety_start_date: string | null;
  terminology: Terminology;
  onboarding_completed: boolean;
  streak: { current: number; best: number; checked_in_today: boolean };
};

export type EntrySummary = {
  id: string;
  mode: "full" | "quick";
  overall_status: StatusKey | null;
  mood_status: StatusKey | null;
  created_at: string;
  item_count: number;
};

export type StatusLabels = { green: string; yellow: string; red: string };

export type Question = {
  id: string;
  source: "template" | "custom";
  question_text: string;
  category: string;
  is_key: boolean;
  labels: StatusLabels;
};

export type Reflection = {
  content: string;
  reading_suggestion: string | null;
  reading_reason: string | null;
  created_at: string;
};

export type EntryItemInput = {
  question_template_id?: string | null;
  custom_question_id?: string | null;
  question_text: string;
  status?: StatusKey | null;
  skipped?: boolean;
};

export type CreateEntryInput = {
  mode?: "full" | "quick";
  mood_status?: StatusKey | null;
  mood_note?: string | null;
  journal_text?: string | null;
  items?: EntryItemInput[];
};

export type CreatedEntry = {
  id: string;
  overall_status: StatusKey | null;
  mode: "full" | "quick";
  created_at: string;
};

/* ---------------------------- endpoints --------------------------- */

export const getDashboard = () => api<Dashboard>("/api/dashboard");

export const listEntries = () =>
  api<{ entries: EntrySummary[] }>("/api/entries").then((r) => r.entries);

export const getQuestions = () =>
  api<{ questions: Question[] }>("/api/questions").then((r) => r.questions);

export const createEntry = (input: CreateEntryInput) =>
  api<CreatedEntry>("/api/entries", {
    method: "POST",
    body: JSON.stringify(input),
  });

export const getReflection = (entryId: string) =>
  api<{ reflection: Reflection | null }>(
    `/api/entries/${entryId}/reflection`
  ).then((r) => r.reflection);

export type EntryItem = {
  id: string;
  question_text: string;
  status: StatusKey | null;
  skipped: number;
  notes: string | null;
};
export type EntryRevision = {
  id: string;
  previous_status: StatusKey;
  new_status: StatusKey;
  notes: string | null;
  created_at: string;
  question_text: string;
};
export type EntryDetail = {
  id: string;
  mode: "full" | "quick";
  overall_status: StatusKey | null;
  mood_status: StatusKey | null;
  mood_note: string | null;
  journal_text: string | null;
  created_at: string;
  items: EntryItem[];
  revisions: EntryRevision[];
};

export const getEntry = (id: string) => api<EntryDetail>(`/api/entries/${id}`);

export const addEntryRevision = (
  entryId: string,
  input: { entry_item_id: string; new_status: StatusKey; notes?: string | null }
) =>
  api<{ id: string; entry_id: string; overall_status: StatusKey | null }>(
    `/api/entries/${entryId}/revisions`,
    { method: "POST", body: JSON.stringify(input) }
  );

export type TrendDay = { date: string; status: StatusKey | null; item_count: number };
export type Trends = {
  range: "day" | "week" | "month";
  days: TrendDay[];
  summary: { green: number; yellow: number; red: number };
  pattern: string;
};

export const getTrends = (range: "day" | "week" | "month") =>
  api<Trends>(`/api/trends?range=${range}`);

/* ------------------------------ lists ------------------------------ */

export type ListSummary = {
  id: string;
  name: string;
  item_count: number;
  last_logged_at: string | null;
  logged_today: boolean;
  trend: (StatusKey | null)[];
};
export type ListItem = { id: string; name: string };
export type ListDetail = {
  list: { id: string; name: string };
  items: ListItem[];
  today_log: Record<string, { status: StatusKey; notes: string | null }>;
  trend: (StatusKey | null)[];
};

export const getLists = () =>
  api<{ lists: ListSummary[] }>("/api/lists").then((r) => r.lists);

export const createList = (name: string) =>
  api<{ id: string; name: string }>("/api/lists", {
    method: "POST",
    body: JSON.stringify({ name }),
  });

export const getList = (id: string) => api<ListDetail>(`/api/lists/${id}`);

export const addListItem = (listId: string, name: string) =>
  api<ListItem>(`/api/lists/${listId}/items`, {
    method: "POST",
    body: JSON.stringify({ name }),
  });

export const logListItem = (
  listId: string,
  input: { item_id: string; status: StatusKey; notes?: string | null }
) =>
  api<{ id: string }>(`/api/lists/${listId}/log`, {
    method: "POST",
    body: JSON.stringify(input),
  });

/* -------------------------- relationships / sharing -------------------------- */

export type ShareLevel = "none" | "individual" | "selected_history" | "full_history";
export type RelationshipRole = "sponsor" | "trusted_contact";

export type OutgoingShare = {
  id: string;
  target_id: string | null;
  target_email: string | null;
  role: string;
  status: "pending" | "active" | "revoked";
  share_level: ShareLevel;
  created_at: string;
  updated_at: string;
};
export type IncomingRequest = {
  id: string;
  requester_id: string;
  requester_name: string;
  role: string;
  status: "pending" | "active" | "revoked";
  share_level: ShareLevel;
  created_at: string;
};

export const getRelationships = () =>
  api<{ outgoing: OutgoingShare[]; incoming: IncomingRequest[] }>("/api/relationships");

export const createRelationship = (input: {
  target_email: string;
  role: RelationshipRole;
  share_level: ShareLevel;
}) =>
  api<OutgoingShare>("/api/relationships", {
    method: "POST",
    body: JSON.stringify(input),
  });

export const setShareLevel = (id: string, share_level: ShareLevel) =>
  api<{ id: string }>(`/api/relationships/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ share_level }),
  });

export const respondRelationship = (id: string, accept: boolean) =>
  api<{ id: string; status: string }>(`/api/relationships/${id}/respond`, {
    method: "POST",
    body: JSON.stringify({ accept }),
  });

export const revokeRelationship = (id: string) =>
  api<{ id: string }>(`/api/relationships/${id}/revoke`, { method: "POST" });

/* --------------------------- sponsor-facing --------------------------- */

export type SponseeSummary = {
  owner_id: string;
  name: string;
  share_level: ShareLevel;
  sobriety_start_date: string | null;
  /** Their own word for their own time, never their sponsor's (§10.4). */
  terminology: Terminology;
  streak: number;
  today_status: StatusKey | null;
  last_check_in: string | null;
  today_note: string | null;
  // Step 4 status metadata for dashboard notices — never content or counts.
  step4: {
    status: "not_started" | "in_progress" | "ready" | "worked";
    started_at: string;
    last_activity: string;
    ready_at: string | null;
    acknowledged_at: string | null;
    wiped_at: string | null;
  } | null;
  step5: { status: "worked"; worked_at: string } | null;
};

export type SponseeDetailData = {
  owner_id: string;
  share_level: ShareLevel;
  name: string;
  sobriety_start_date: string | null;
  terminology: Terminology;
  streak: number;
  checked_in_today: boolean;
  week: { date: string; status: StatusKey | null }[];
  strip: (StatusKey | null)[];
  entries: {
    id: string;
    overall_status: StatusKey | null;
    item_count: number;
    created_at: string;
    note: string | null;
    note_private: boolean;
  }[];
  ideas: { content: string; created_at: string }[];
  steps: SponseeStepSummary[];
};

export type SponseeStepSummary = {
  step_number: number;
  name: string;
  principle: string | null;
  // "awaiting" is Step 5's synthesized state — the sitting hasn't happened
  // yet, so the sponsee has no step_work row (server-side, sponsor.ts).
  status: "not_started" | "in_progress" | "ready" | "worked" | "awaiting";
  is_shared: number;
  ready_to_talk: number;
  updated_at: string;
};

export type SponseeStepWork = {
  step_number: number;
  name: string;
  principle: string | null;
  status: "ready" | "worked" | string;
  is_shared: number;
  ready_to_talk: number;
  acknowledged_at: string | null;
  updated_at: string;
  willingness_note: string | null;
  // What this step calls its fourth phase, for the card heading above.
  willingness_label?: string | null;
  // Step 4/5 only — status metadata; content never crosses this API.
  started_at?: string;
  last_activity?: string | null;
  wiped_at?: string | null;
  worked_at?: string | null;
  s4_ready_at?: string | null;
  s4_wiped_at?: string | null;
  my_note?: { note_text: string; created_at: string } | null;
  reflections: {
    id: string;
    prompt_text: string;
    answer_text: string;
    created_at: string;
    additions: { added_text: string; created_at: string }[];
  }[];
};

export const getSponseeStep = (ownerId: string, n: number) =>
  api<SponseeStepWork>(`/api/sponsees/${ownerId}/steps/${n}`);

export const saveSponseeStepNote = (ownerId: string, n: number, note: string) =>
  api<{ saved: boolean }>(`/api/sponsees/${ownerId}/steps/${n}/note`, {
    method: "PUT",
    body: JSON.stringify({ note }),
  });

export const acknowledgeSponseeStep = (ownerId: string, n: number, note?: string) =>
  api<{ step_number: number; status: string }>(
    `/api/sponsees/${ownerId}/steps/${n}/acknowledge`,
    { method: "POST", body: JSON.stringify(note ? { note } : {}) }
  );

export const getSponsees = () =>
  api<{ sponsees: SponseeSummary[] }>("/api/sponsees").then((r) => r.sponsees);

export const getSponseeDetail = (ownerId: string) =>
  api<SponseeDetailData>(`/api/sponsees/${ownerId}`);

export const sendSponseeNote = (ownerId: string, message: string) =>
  api<{ sent: boolean }>(`/api/sponsees/${ownerId}/note`, {
    method: "POST",
    body: JSON.stringify({ message }),
  });

export type IdeaReflectionResponse =
  | "still_feels_right"
  | "funny_in_hindsight"
  | "worth_revisiting";

export type BrightIdea = {
  id: string;
  content: string;
  tags: string[];
  ai_analysis: string | null;
  analysis_created_at: string | null;
  reflection_response_type: IdeaReflectionResponse | null;
  reflection_response_note: string | null;
  created_at: string;
};

export const listBrightIdeas = () =>
  api<{ bright_ideas: BrightIdea[] }>("/api/bright-ideas").then((r) => r.bright_ideas);

export const createBrightIdea = (content: string) =>
  api<{ id: string }>("/api/bright-ideas", {
    method: "POST",
    body: JSON.stringify({ content }),
  });

export const respondBrightIdea = (
  id: string,
  response_type: IdeaReflectionResponse,
  note?: string | null
) =>
  api<{ id: string }>(`/api/bright-ideas/${id}/reflection`, {
    method: "POST",
    body: JSON.stringify({ response_type, note: note ?? null }),
  });

/* ------------------------ fellowships / literature ------------------------ */

/**
 * Which literature the person uses (Fellowships PRD §4) — asked as literature,
 * never as identity: optional, multi-select, changeable, never a gate. An empty
 * selection is a first-class state: readings then say to open your own book,
 * and the app never falls back to AA's citations (§6).
 */
export type FellowshipId = "aa" | "na" | "ca" | "ga" | "oa";

/** The word this account uses for time — sober / clean / abstinent, or neutral. */
export type Terminology = {
  adj: string;
  noun: string;
  /** Label for the date field: "Clean date", "Sobriety date", … */
  date: string;
  /** Heading on the milestone card: "Clean Time", "Recovery Time", … */
  card: string;
  /** True when nothing is selected, or selections disagree: no word is chosen. */
  neutral: boolean;
};

export type FellowshipOption = {
  id: FellowshipId;
  name: string;
  short: string;
  /** Descriptive help so someone recognises their own books. Cites nothing. */
  books: string;
  term: { adj: string; noun: string; date: string };
};

export const getFellowships = () =>
  api<{ fellowships: FellowshipOption[] }>("/api/fellowships").then((r) => r.fellowships);

/** One reading for one step, resolved from the user's own selections. */
export type StepCitation = {
  fellowship: FellowshipId;
  short: string;
  citation: string;
  citation_note: string | null;
  also_citation: string | null;
  also_note: string | null;
  /** Which edition the page range refers to, shown beside the citation. */
  edition: string | null;
  /** Safe for the AI to cite; unverified sets stay chapter-level. */
  verified: boolean;
};

/* ------------------------ twelve-step work ------------------------ */

export type StepCatalog = {
  step_number: number;
  name: string;
  principle: string | null;
  intro_text: string | null;
  // reading_citation / also_citation / also_note are NOT here: the reading card
  // resolves from `citations` (per the user's literature), and the catalog's
  // pre-fellowship copies are no longer sent. See worker/steps.ts getStep.
  supporting_note: string | null;
  // A second Overview note, rendered with a lock icon (per-step opt-in).
  privacy_note: string | null;
  // A note on the Reading screen, rendered with a book icon (per-step opt-in).
  reading_note: string | null;
  // What the fourth phase is called for this step — "Willingness" for Steps
  // 1–3 and 6, "The asking" for Step 7. Content, not chrome.
  willingness_label: string | null;
};

export type StepState = "worked" | "ready" | "in_progress" | "available" | "locked";
export type StepOverviewItem = {
  step_number: number;
  name: string;
  principle: string | null;
  state: StepState;
  current_phase: string | null;
  updated_at: string | null;
  wiped_at: string | null;
  // So "you left off at …" can name the fourth phase this step's own way.
  willingness_label: string | null;
};

export const getStepsOverview = () =>
  api<{ steps: StepOverviewItem[] }>("/api/steps").then((r) => r.steps);

export type StepPrompt = {
  id: string;
  sort_order: number;
  prompt_text: string;
  note_text: string | null;
};

export type StepWork = {
  id: string;
  step_number: number;
  status: "not_started" | "in_progress" | "ready" | "worked";
  current_phase: string | null;
  reading_read_on: string | null;
  reading_impressions: string | null;
  reading_acknowledged: number;
  willingness_note: string | null;
  willingness_affirmed: number;
  is_shared: number;
  ready_to_talk: number;
  wiped_at: string | null;
  updated_at: string;
};

export type StepReflection = {
  id: string;
  step_prompt_id: string | null;
  prompt_text: string;
  answer_text: string;
  created_at: string;
  additions: { id: string; added_text: string; created_at: string }[];
};

export type StepData = {
  step: StepCatalog;
  prompts: StepPrompt[];
  work: StepWork | null;
  reflections: StepReflection[];
  available: boolean;
  /** The reading card's contents. Empty = "your own reading" (§10.3). */
  citations: StepCitation[];
  fellowships: FellowshipId[];
  terminology: Terminology;
};

export const getStep = (n: number) => api<StepData>(`/api/steps/${n}`);

/* ------------- Step 4: the encrypted inventory (ciphertext only) ------------- */

export type Step4Pass = "resentments" | "fears" | "harms";
export type Step4Row = {
  id: string;
  pass: Step4Pass;
  ciphertext: string;
  created_at: string;
  updated_at: string;
};

export const getStep4Rows = () => api<{ rows: Step4Row[] }>(`/api/steps/4/rows`);

export const addStep4Row = (pass: Step4Pass, ciphertext: string) =>
  api<Step4Row>(`/api/steps/4/rows`, {
    method: "POST",
    body: JSON.stringify({ pass, ciphertext }),
  });

export const updateStep4Row = (id: string, ciphertext: string) =>
  api<Step4Row>(`/api/step4-rows/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ ciphertext }),
  });

export const wipeStep4 = () =>
  api<{ deleted_rows: number }>(`/api/steps/4/rows`, { method: "DELETE" });

/* ------------- Step 8: the encrypted amends list (ciphertext only) -------------
 * Everything about a person — who, what happened, my part, willingness, the
 * talk-it-through marker, the private note — is inside `ciphertext`. Unlike
 * every other step's writing, rows are mutable and individually deletable
 * (AUTHORIZATION_SPEC §Step 8).
 */

export type Step8Row = {
  id: string;
  ciphertext: string;
  created_at: string;
  updated_at: string;
};

/** The decrypted shape of a row, as written by this device. */
export type Step8Willingness = "not" | "working" | "willing";
/**
 * Step 9's per-person outcome, carried on the same row (Step 9 PRD §2, §3).
 * None of these is ranked, and none is a failure: "notyet" is the resting
 * state however long it lasts, and "notmaking" — the harm-avoidance carve-out
 * decided with a sponsor — is a COMPLETE answer, never an open to-do.
 */
export type Step9Outcome = "notyet" | "planned" | "made" | "another" | "notmaking";

export type Step8Person = {
  who: string;
  harm: string;
  my_part: string;
  note: string;
  willingness: Step8Willingness;
  /** "Talk this one through with my sponsor" — a marker, never a message. */
  flagged: boolean;
  /** Step 9 writes these two onto the same encrypted row. */
  outcome: Step9Outcome;
  /** Where things stand — never what was said (§3). */
  outcome_note: string;
};

export const getStep8Rows = () => api<{ rows: Step8Row[] }>(`/api/steps/8/rows`);

export const addStep8Row = (ciphertext: string) =>
  api<Step8Row>(`/api/steps/8/rows`, {
    method: "POST",
    body: JSON.stringify({ ciphertext }),
  });

export const updateStep8Row = (id: string, ciphertext: string) =>
  api<Step8Row>(`/api/step8-rows/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ ciphertext }),
  });

export const deleteStep8Row = (id: string) =>
  api<{ deleted: boolean }>(`/api/step8-rows/${id}`, { method: "DELETE" });

export const deleteStep8List = () =>
  api<{ deleted_rows: number }>(`/api/steps/8/rows`, { method: "DELETE" });

export const startStep = (n: number) =>
  api<StepWork>(`/api/steps/${n}/start`, { method: "POST" });

export const updateStep = (n: number, patch: Partial<Record<string, unknown>>) =>
  api<StepWork>(`/api/steps/${n}`, { method: "PATCH", body: JSON.stringify(patch) });

export const addStepReflection = (
  n: number,
  input: { step_prompt_id?: string | null; prompt_text: string; answer_text: string }
) =>
  api<StepReflection>(`/api/steps/${n}/reflections`, {
    method: "POST",
    body: JSON.stringify(input),
  });

export const addReflectionAddition = (reflectionId: string, added_text: string) =>
  api<{ id: string }>(`/api/step-reflections/${reflectionId}/additions`, {
    method: "POST",
    body: JSON.stringify({ added_text }),
  });

/* ---------------------- twenty questions ---------------------- */

export type TwentyQuestion = {
  id: string;
  sort_order: number;
  question_text: string;
};

export type TwentyAssessment = {
  id: string;
  status: "in_progress" | "completed";
  yes_count: number | null;
  answers: Record<string, "yes" | "no">;
};

export type TwentyData = {
  questions: TwentyQuestion[];
  assessment: TwentyAssessment | null;
};

export const getTwentyQuestions = () => api<TwentyData>("/api/twenty-questions");

export const saveTwentyAnswer = (question_id: string, answer: "yes" | "no") =>
  api<{ assessment_id: string }>("/api/twenty-questions/answers", {
    method: "POST",
    body: JSON.stringify({ question_id, answer }),
  });

export const completeTwentyQuestions = () =>
  api<{ id: string; yes_count: number }>("/api/twenty-questions/complete", {
    method: "POST",
  });

export const restartTwentyQuestions = () =>
  api<{ id: string }>("/api/twenty-questions/restart", { method: "POST" });

/* ----------------------------- settings ----------------------------- */

export type Settings = {
  profile: {
    display_name: string;
    sobriety_start_date: string | null;
    onboarding_completed: boolean;
    /** Which literature the person uses — empty is a first-class answer (§4). */
    fellowships: FellowshipId[];
    terminology: Terminology;
  };
  reminders: { reminder_time: string; timezone: string; is_active: number } | null;
};

export const getSettings = () => api<Settings>("/api/settings");

export const updateProfile = (patch: {
  display_name?: string;
  sobriety_start_date?: string | null;
  onboarding_completed?: boolean;
  /** Pass [] to clear — never a gate, always changeable (§4). */
  fellowships?: FellowshipId[];
}) =>
  api<Settings>("/api/settings/profile", {
    method: "PATCH",
    body: JSON.stringify(patch),
  });

export const updateReminders = (input: {
  reminder_time: string;
  timezone: string;
  is_active: boolean;
}) =>
  api<Settings>("/api/settings/reminders", {
    method: "PUT",
    body: JSON.stringify(input),
  });

export const deleteAccount = () =>
  api<{ deleted: boolean }>("/api/account", { method: "DELETE" });
